import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { useQueryState } from 'react-router-use-location-state';
import {
    ModalControl,
    ModalProvider,
    InternalGrid,
    Box,
} from '@paygreen/paygreen-ui';
import {
    H1Title,
    ErrorBoundary,
    ToggleMode,
    SimpleActionButton,
} from '../../components';
import { useAPIError, useMode } from '../../context';
import { formatDateApi, IsBigDesktop, IsDesktop } from '../../utils/tools';
import { endOfMonth, startOfMonth, MODES } from '../../utils/constants';
import DonationsTable from './DonationsTable';
import ExportDonationsModal from './ExportDonationsModal';
import FilterTable from './FilterTable';
import { getAllDonations } from './service';

const ShopDonations = ({ t }) => {
    const { addError } = useAPIError();
    const { mode } = useMode();
    const fetchIdRef = useRef(0);

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [activePageReset, setActivePageReset] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');

    const [startDate, setStartDate] = useQueryState('startDate', startOfMonth);
    const [endDate, setEndDate] = useQueryState('endDate', endOfMonth);

    // to reset pageIndex and filters when mode changes
    const handleModeChange = () => {
        setActivePageReset(true);
        setStatusFilter('');
    };

    const fetchData = useCallback(
        ({ pageSize, pageIndex, statusFilter, startDate, endDate }) => {
            const fetchId = ++fetchIdRef.current;
            if (fetchId === fetchIdRef.current) {
                isMounted.current && setIsLoading(true);

                const params = {
                    limit: pageSize,
                    page: pageIndex + 1,
                    status: statusFilter,
                    begin: startDate && formatDateApi(startDate),
                    end: endDate && formatDateApi(endDate),
                };

                getAllDonations(params, mode)
                    .then((res) => {
                        const {
                            page_count,
                            total_items,
                            _embedded: { donation },
                        } = res.data;

                        if (isMounted.current) {
                            setData(donation);
                            setPageCount(page_count);
                            setTotalItems(total_items);
                        }
                    })
                    .catch((error) => {
                        const { detail, status } = error?.response?.data;

                        if (status === 404) {
                            isMounted.current && setData([]);
                        } else {
                            addError(detail, status);
                        }
                    })
                    .finally(() => {
                        isMounted.current && setIsLoading(false);
                    });
            }
        },
        [addError, mode],
    );

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>
                    {t('shopDonations.metaTitle')} - {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title title={t('shopDonations.title')}>
                <InternalGrid
                    displayType="grid"
                    gridTemplateColumns="auto auto"
                    gridGap="md"
                    alignItems="center"
                >
                    <ToggleMode externalChange={handleModeChange} />

                    <ModalProvider>
                        {IsDesktop() && mode === MODES.PROD && (
                            <ModalControl hasOnClickControl={true}>
                                <SimpleActionButton icon="DownloadIcon">
                                    {t('form.button.export')}
                                </SimpleActionButton>
                            </ModalControl>
                        )}

                        <ModalControl>
                            <ExportDonationsModal
                                startDate={startDate}
                                endDate={endDate}
                            />
                        </ModalControl>
                    </ModalProvider>
                </InternalGrid>
            </H1Title>

            <InternalGrid
                displayType="grid"
                gridGap="md"
                gridTemplateColumns="1fr"
                blockPadding="none"
                justifyItems="stretch"
                hasStaticWidth={IsBigDesktop() ? false : true}
            >
                <Box
                    marginBottom={IsDesktop() ? 'lg' : 'md'}
                    style={{
                        width: 'fit-content',
                    }}
                >
                    <FilterTable
                        setEndDate={setEndDate}
                        setStartDate={setStartDate}
                        startDate={startDate}
                        endDate={endDate}
                    />
                </Box>

                <ErrorBoundary>
                    <DonationsTable
                        fetchData={fetchData}
                        data={data}
                        loading={isLoading}
                        pageCount={pageCount}
                        startDate={startDate}
                        endDate={endDate}
                        totalItems={totalItems}
                        setActivePageReset={setActivePageReset}
                        activePageReset={activePageReset}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                    />
                </ErrorBoundary>
            </InternalGrid>
        </>
    );
};

export default withTranslation('common')(ShopDonations);
